import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AgentCardData,
  AgentDetail,
  AgentSpecRow,
  ActivityEntry,
  BuildJobDescriptionResult,
  ChatMessage,
  ColleagueRow,
  ContentVersionRow,
  FileAttachment,
  CreateColleagueRequest,
  CreateUserRequest,
  ImportSpecTextRequest,
  ImportSpecTextResult,
  DbStatRow,
  DispatchGraph,
  DockerContainerInfo,
  HostMetrics,
  OllamaModelInfo,
  InfraSettingRow,
  InfraSettingUpdate,
  LearnedRuleRow,
  Paged,
  PublicUser,
  SandmanProposalRow,
  ScheduledTaskRow,
  SeedSpecFromXmlResult,
  SessionRow,
  SkillImportEntry,
  SkillImportResult,
  SkillRow,
  UpdateMeRequest,
  UpdateSessionRequest,
  UpdateUserRequest,
  UpsertRuleRequest,
  UpsertScheduleRequest,
  UpsertSkillRequest,
  LlmEnvSnapshot,
  LlmEnvUpdate,
  BackupEntry,
  UsageStats,
  DepartmentWithAgents,
  DepartmentRow,
  CreateDepartmentRequest,
  UpdateDepartmentRequest,
} from '@command-room/shared';
import { api, clearSessionExpired, markSessionExpired, SESSION_EXPIRED_MESSAGE } from './client';

// --- Auth ---
export function useMe() {
  return useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.get<{ user: PublicUser }>('/auth/me'),
    retry: false,
    staleTime: 60_000,
    // Notices an expired session on pages that poll nothing else, and on
    // every return to the tab even while the cached user is still fresh.
    refetchInterval: 60_000,
    refetchOnWindowFocus: 'always',
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; password: string }) =>
      api.post<{ user: PublicUser }>('/auth/login', body),
    onSuccess: () => {
      clearSessionExpired();
      return qc.invalidateQueries({ queryKey: ['auth'] });
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<void>('/auth/logout'),
    onSuccess: () => {
      clearSessionExpired();
      qc.clear();
    },
  });
}

export function useUpdateMe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateMeRequest) => api.patch<{ user: PublicUser }>('/auth/me', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['auth'] }),
  });
}

export function useUploadAvatar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('avatar', file);
      return fetch('/api/auth/me/avatar', {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-Requested-With': 'command-room' },
        body: form,
      }).then(async (res) => {
        if (res.status === 401) {
          markSessionExpired();
          throw new Error(SESSION_EXPIRED_MESSAGE);
        }
        if (!res.ok) {
          const err = (await res.json().catch(() => ({}))) as { message?: string };
          throw new Error(err.message ?? res.statusText);
        }
        return res.json() as Promise<{ user: PublicUser }>;
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['auth'] }),
  });
}

// --- Agents ---
export function useAgents() {
  return useQuery({
    queryKey: ['agents'],
    queryFn: () => api.get<AgentCardData[]>('/agents'),
    refetchInterval: 10_000,
  });
}

export function useDepartments() {
  return useQuery({
    queryKey: ['departments'],
    queryFn: () => api.get<DepartmentWithAgents[]>('/departments'),
    refetchInterval: 30_000,
  });
}

export function useCreateDepartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateDepartmentRequest) => api.post<DepartmentRow>('/departments', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['departments'] }),
  });
}

export function useUpdateDepartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: UpdateDepartmentRequest & { id: number }) =>
      api.patch<DepartmentRow>(`/departments/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['departments'] });
      qc.invalidateQueries({ queryKey: ['agents'] });
    },
  });
}

export function useDeleteDepartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.del<void>(`/departments/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['departments'] });
      qc.invalidateQueries({ queryKey: ['agents'] });
    },
  });
}

export function useCreateColleague() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateColleagueRequest) => api.post<ColleagueRow>('/agents', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agents'] });
      qc.invalidateQueries({ queryKey: ['departments'] });
    },
  });
}

/**
 * Import a pasted job description / agent spec into an agent's spec sections.
 * The agent name is a mutation variable rather than a hook argument so it can be
 * supplied in the same tick the agent is created.
 */
export function useImportSpecText() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ name, ...body }: ImportSpecTextRequest & { name: string }) =>
      api.post<ImportSpecTextResult>(`/agents/${encodeURIComponent(name)}/spec/import`, body),
    onSuccess: (_data, vars) => {
      if (!vars.dry_run) qc.invalidateQueries({ queryKey: ['agents', vars.name] });
    },
  });
}

export function useAgent(name: string | undefined) {
  return useQuery({
    queryKey: ['agents', name],
    queryFn: () => api.get<AgentDetail>(`/agents/${encodeURIComponent(name!)}`),
    enabled: !!name,
    refetchInterval: 15_000,
  });
}

export function useUpdateAgent(name: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.patch<AgentDetail>(`/agents/${encodeURIComponent(name)}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agents'] });
    },
  });
}

export function useUpdateAgentSpec(name: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<Omit<AgentSpecRow, 'agent_name' | 'updated_by' | 'updated_at'>>) =>
      api.patch<AgentDetail>(`/agents/${encodeURIComponent(name)}/spec`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agents', name] }),
  });
}

// --- Sessions ---
export function useSessions(status: 'open' | 'resolved' | 'all' = 'open') {
  return useQuery({
    queryKey: ['sessions', status],
    queryFn: () => api.get<SessionRow[]>(`/sessions?status=${status}`),
    refetchInterval: 10_000,
  });
}

export function useSession(id: string | undefined) {
  return useQuery({
    queryKey: ['sessions', id],
    queryFn: () => api.get<SessionRow>(`/sessions/${encodeURIComponent(id!)}`),
    enabled: !!id,
  });
}

export function useSessionMessages(id: string | undefined) {
  return useQuery({
    queryKey: ['sessions', id, 'messages'],
    queryFn: () => api.get<{ messages: ChatMessage[]; files: FileAttachment[] }>(`/sessions/${encodeURIComponent(id!)}/messages`),
    enabled: !!id,
  });
}

export function useUpdateSession(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateSessionRequest) =>
      api.patch<SessionRow>(`/sessions/${encodeURIComponent(id)}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sessions'] }),
  });
}

// --- Activity + dispatch ---
export function useActivity(limit = 100) {
  return useQuery({
    queryKey: ['activity', limit],
    queryFn: () => api.get<Paged<ActivityEntry>>(`/activity?limit=${limit}`),
    refetchInterval: 15_000,
  });
}

export function useDispatchGraph(days = 7) {
  return useQuery({
    queryKey: ['dispatch', 'graph', days],
    queryFn: () => api.get<DispatchGraph>(`/dispatch/graph?days=${days}`),
    refetchInterval: 30_000,
  });
}

// --- Skills ---
export function useSkills() {
  return useQuery({ queryKey: ['skills'], queryFn: () => api.get<SkillRow[]>('/skills') });
}

export function useUpsertSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpsertSkillRequest & { id?: number }) =>
      body.id
        ? api.patch<SkillRow>(`/skills/${body.id}`, body)
        : api.post<SkillRow>('/skills', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['skills'] }),
  });
}

export function useDeleteSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.del<void>(`/skills/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['skills'] }),
  });
}

export function useAssignSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ agentName, skillId }: { agentName: string; skillId: string }) =>
      api.post<void>(`/agents/${encodeURIComponent(agentName)}/skills/${encodeURIComponent(skillId)}`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['skills'] });
      qc.invalidateQueries({ queryKey: ['agents'] });
    },
  });
}

export function useUnassignSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ agentName, skillId }: { agentName: string; skillId: string }) =>
      api.del<void>(`/agents/${encodeURIComponent(agentName)}/skills/${encodeURIComponent(skillId)}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['skills'] });
      qc.invalidateQueries({ queryKey: ['agents'] });
    },
  });
}

// --- Rules ---
export function useRules(scope?: string, target?: string, status: string = 'active') {
  return useQuery({
    queryKey: ['rules', scope ?? 'all', target ?? 'all', status],
    queryFn: () => {
      const params = new URLSearchParams();
      if (scope) params.set('scope', scope);
      if (target) params.set('target', target);
      if (status) params.set('status', status);
      const qs = params.toString();
      return api.get<LearnedRuleRow[]>(`/rules${qs ? `?${qs}` : ''}`);
    },
  });
}

export function useUpsertRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpsertRuleRequest & { id?: number }) =>
      body.id
        ? api.patch<LearnedRuleRow>(`/rules/${body.id}`, body)
        : api.post<LearnedRuleRow>('/rules', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rules'] }),
  });
}

export function useDeleteRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.del<void>(`/rules/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rules'] }),
  });
}

export function useBulkDeleteRules() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) => api.del<{ deleted: number }>('/rules', { body: { ids } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rules'] }),
  });
}

// --- Sandman proposals ---
export function useProposals(status: string = 'pending') {
  return useQuery({
    queryKey: ['proposals', status],
    queryFn: () => api.get<SandmanProposalRow[]>(`/proposals?status=${status}`),
  });
}

export function useDecideProposal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, decision }: { id: number; decision: 'approved' | 'rejected' }) =>
      api.post<SandmanProposalRow>(`/proposals/${id}/decide`, { decision }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['proposals'] });
      qc.invalidateQueries({ queryKey: ['rules'] });
      qc.invalidateQueries({ queryKey: ['skills'] });
      qc.invalidateQueries({ queryKey: ['agents'] });
    },
  });
}

// --- Content versions ---
export function useVersions(contentKind: 'skill' | 'agent_spec', contentRef: string | undefined) {
  return useQuery({
    queryKey: ['versions', contentKind, contentRef],
    queryFn: () =>
      api.get<ContentVersionRow[]>(`/versions?content_kind=${contentKind}&content_ref=${encodeURIComponent(contentRef!)}`),
    enabled: !!contentRef,
  });
}

export function useRestoreVersion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (versionId: number) =>
      api.post<{ restored: boolean; content_kind: string; content_ref: string }>(`/versions/${versionId}/restore`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['skills'] });
      qc.invalidateQueries({ queryKey: ['agents'] });
      qc.invalidateQueries({ queryKey: ['rules'] });
      qc.invalidateQueries({ queryKey: ['versions'] });
    },
  });
}

// --- Scheduler ---
export function useSchedule(agent?: string) {
  return useQuery({
    queryKey: ['schedule', agent ?? 'all'],
    queryFn: () => api.get<ScheduledTaskRow[]>(`/schedule${agent ? `?agent=${agent}` : ''}`),
    refetchInterval: 30_000,
  });
}

export function useUpsertSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpsertScheduleRequest & { id?: string }) =>
      body.id
        ? api.patch<ScheduledTaskRow>(`/schedule/${body.id}`, body)
        : api.post<ScheduledTaskRow>('/schedule', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['schedule'] }),
  });
}

export function useTriggerSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<void>(`/schedule/${id}/trigger`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['activity'] }),
  });
}

export function useDeleteSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<void>(`/schedule/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['schedule'] }),
  });
}

// --- Stats ---
export function useDbStats() {
  return useQuery({
    queryKey: ['stats', 'db'],
    queryFn: () => api.get<DbStatRow[]>('/stats/db'),
    refetchInterval: 60_000,
  });
}

export function useHostStats() {
  return useQuery({
    queryKey: ['stats', 'host'],
    queryFn: () => api.get<HostMetrics>('/stats/host'),
    refetchInterval: 5_000,
  });
}

export function useDockerStats() {
  return useQuery({
    queryKey: ['stats', 'docker'],
    queryFn: () => api.get<DockerContainerInfo[]>('/stats/docker'),
    refetchInterval: 10_000,
  });
}

export function useOllamaModels() {
  return useQuery({
    queryKey: ['stats', 'ollama'],
    queryFn: () => api.get<OllamaModelInfo[]>('/stats/ollama'),
    refetchInterval: 30_000,
  });
}

export function useRestartContainer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<void>(`/settings/docker/${id}/restart`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stats', 'docker'] }),
  });
}

export function usePullOllamaModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api.post<void>('/settings/ollama/pull', { name }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stats', 'ollama'] }),
  });
}

export function useRemoveOllamaModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api.del<void>(`/settings/ollama/${encodeURIComponent(name)}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stats', 'ollama'] }),
  });
}

export function useUsageStats(range: '7d' | '30d' | '90d' | 'all' = '30d', agent?: string) {
  return useQuery({
    queryKey: ['stats', 'usage', range, agent ?? 'all'],
    queryFn: () =>
      api.get<UsageStats>(`/stats/usage?range=${range}${agent ? `&agent=${encodeURIComponent(agent)}` : ''}`),
    refetchInterval: 60_000,
  });
}

// --- Settings ---
export function useEnvSettings() {
  return useQuery({
    queryKey: ['settings', 'env'],
    queryFn: () => api.get<LlmEnvSnapshot>('/settings/env'),
  });
}

export function useUpdateEnvSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: LlmEnvUpdate) => api.patch<LlmEnvSnapshot>('/settings/env', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings', 'env'] }),
  });
}

export function useRestartN8n() {
  return useMutation({ mutationFn: () => api.post<void>('/settings/n8n/restart') });
}

export function useBackups() {
  return useQuery({
    queryKey: ['settings', 'backups'],
    queryFn: () => api.get<BackupEntry[]>('/settings/backup'),
  });
}

export function useCreateBackup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<BackupEntry>('/settings/backup'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings', 'backups'] }),
  });
}

export function useInfraSettings() {
  return useQuery({
    queryKey: ['settings', 'infrastructure'],
    queryFn: () => api.get<InfraSettingRow[]>('/settings/infrastructure'),
    staleTime: 60_000,
  });
}

export function useUpdateInfraSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: InfraSettingUpdate) =>
      api.patch<InfraSettingRow[]>('/settings/infrastructure', body),
    onSuccess: (rows) => qc.setQueryData(['settings', 'infrastructure'], rows),
  });
}

/** Write the stored organization description back to _parts/organization.xml on disk. */
export function useExportOrganization() {
  return useMutation({
    mutationFn: () =>
      api.post<{ written: boolean; path: string }>('/settings/organization/export'),
  });
}

// --- Users (admin) ---
export function useUsers() {
  return useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<PublicUser[]>('/users'),
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateUserRequest) => api.post<PublicUser>('/users', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: number } & UpdateUserRequest) =>
      api.patch<PublicUser>(`/users/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

// --- Agent avatar import ---
export function useImportAgentAvatar(name: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (url: string) =>
      api.post<{ agent_icon_url: string }>(`/agents/${encodeURIComponent(name)}/avatar`, { url }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agents', name] });
      qc.invalidateQueries({ queryKey: ['agents'] });
    },
  });
}

// --- Agent spec extras ---
export function useSeedSpecFromXml(name: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ force = false }: { force?: boolean } = {}) =>
      api.post<SeedSpecFromXmlResult>(
        `/agents/${encodeURIComponent(name)}/spec/seed-from-xml${force ? '?force=true' : ''}`,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agents', name] }),
  });
}

export function useBuildJobDescription(name: string) {
  return useMutation({
    mutationFn: ({ write = false }: { write?: boolean } = {}) =>
      api.post<BuildJobDescriptionResult>(
        `/agents/${encodeURIComponent(name)}/spec/build${write ? '?write=true' : ''}`,
      ),
  });
}

// --- Skills bulk import ---
export function useImportSkills() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (skills: SkillImportEntry[]) =>
      api.post<SkillImportResult>('/skills/import', { skills }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['skills'] }),
  });
}

// --- Re-embed ---
export function useReembedSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<void>(`/skills/${id}/reembed`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['skills'] }),
  });
}

export function useReembedRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<void>(`/rules/${id}/reembed`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rules'] }),
  });
}
